import React, { forwardRef, ReactNode, useImperativeHandle, useRef, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Autocomplete,
  Box,
  IconButton,
  InputAdornment,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  EconsultManagedSpecialtyProgram,
  EconsultUtils,
  NameUtils,
  UserDTO
} from '@oh-vcp/components-common';
import { TextFieldNoOutline } from '@oh-vcp/components-ui';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';

interface EconsultRedirectSpecialistSearchProps {
  options: (UserDTO | EconsultManagedSpecialtyProgram)[];
  onInputChange: (inputValue: string) => void;
  onSelectionChange?: (selectedValue: UserDTO | EconsultManagedSpecialtyProgram | null) => void;
  searchValue: UserDTO | EconsultManagedSpecialtyProgram | null;
}

export interface EconsultRedirectSpecialistSearchRef {
  clear: () => void;
  validate: () => boolean;
}

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    width: 400,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9'
  }
}));

const EconsultRedirectSpecialistSearchTextField = styled(TextFieldNoOutline)({
  '& .MuiOutlinedInput-root': {
    paddingLeft: 0,
    '&.Mui-focused fieldset': {
      borderColor: 'transparent'
    }
  },
  '& .MuiOutlinedInput-input': {
    paddingLeft: '0px !important'
  },
  '& .MuiInputBase-input': {
    fontSize: '0.875rem'
  },
  '& .MuiInputBase-input::placeholder': {
    fontSize: '0.875rem'
  }
});
const vaildFieldMessageType = {
  display: 'none'
};
const inVaildFieldMessageType = {
  display: 'block',
  color: 'red',
  fontSize: '0.75rem'
};
const EconsultRedirectSpecialistSearch = forwardRef<
  EconsultRedirectSpecialistSearchRef,
  EconsultRedirectSpecialistSearchProps
>((props, ref) => {
  const { t } = useTranslation();
  const { options, onInputChange, onSelectionChange, searchValue } = props;
  const [internalInputValue, setInternalInputValue] = React.useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [requiredErrorMessageStyle, setRequiredErrorMessageStyle] = useState(vaildFieldMessageType);
  useImperativeHandle(ref, () => ({
    clear() {
      setInternalInputValue('');
    },
    validate() {
      const valid = internalInputValue !== '';
      if (!valid) {
        setRequiredErrorMessageStyle(inVaildFieldMessageType);
      }
      return valid;
    }
  }));

  const getOptionLabel = (option: UserDTO | EconsultManagedSpecialtyProgram | string) => {
    if (typeof option === 'string') {
      return option;
    }
    if ('programName' in option) {
      const specialtyProgram = option as EconsultManagedSpecialtyProgram;
      return `${specialtyProgram.programName}${
        specialtyProgram.organization ? `, ${specialtyProgram.organization}` : ''
      }`;
    }
    return NameUtils.makeName(
      option.lastName || '',
      option.firstName,
      option.salutation,
      option.specialty,
      option.middleName,
      null,
      option.city,
      option.lhin?.number?.toString(),
      option?.unavailability
    );
  };

  const getLabelColor = (isDisabled: boolean, isHighlight: boolean) => {
    if (isDisabled) return '#000000';
    if (isHighlight) return 'primary.custom5';
    return 'primary.main';
  };

  const getTooltipInfo = (
    option: UserDTO | EconsultManagedSpecialtyProgram | string
  ): ReactNode => {
    if (typeof option === 'string') {
      return null;
    }
    if ('programName' in option) {
      return (
        <Box>
          {(option.econCondRegion || option.econCondition) && (
            <Typography variant="body1b">{t('Case.tooltip.caseConditions')}</Typography>
          )}
          {option.econCondRegion && (
            <Box mb={2}>
              <Typography variant="body2">{t('Case.tooltip.onlyAcceptedFromRegions')}:</Typography>
              <Typography variant="body2">{option.econCondRegion}</Typography>
            </Box>
          )}
          {option.econCondition && (
            <Box mb={2}>
              <Typography variant="body2">{t('Case.tooltip.otherConditions')}:</Typography>
              <Typography variant="body2">{option.econCondition}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="body1b">{t('Case.tooltip.aboutGroup')}</Typography>
            <Typography variant="body2">{option.programDescription}</Typography>
          </Box>
        </Box>
      );
    }
    if (
      option.specializedAreaOfPractice ||
      (option.econsult && (option.econsult.catchmentArea || option.econsult.otherConditions)) ||
      option.patientEligibility
    ) {
      return (
        <Box>
          {option.specializedAreaOfPractice && (
            <Box mb={2}>
              <Typography variant="body1b">{t('Case.tooltip.tmServiceDetails')}</Typography>
              <Typography variant="body2">{option.specializedAreaOfPractice}</Typography>
            </Box>
          )}
          {option.econsult &&
            (option.econsult.catchmentArea || option.econsult.otherConditions) && (
              <>
                <Typography variant="body1b">{t('Case.tooltip.caseConditions')}</Typography>
                {option.econsult.catchmentArea && (
                  <Box mb={2}>
                    <Typography variant="body2">
                      {t('Case.tooltip.onlyAcceptedFromRegions')}:
                    </Typography>
                    <Typography variant="body2">{option.econsult.catchmentArea}</Typography>
                  </Box>
                )}
                {option.econsult.otherConditions && (
                  <Box mb={2}>
                    <Typography variant="body2">{t('Case.tooltip.otherConditions')}:</Typography>
                    <Typography variant="body2">{option.econsult.otherConditions}</Typography>
                  </Box>
                )}
              </>
            )}
          {option.patientEligibility && (
            <Box>
              <Typography variant="body1b">{t('Case.tooltip.patientEligibility')}</Typography>
              <Typography variant="body2">{option.patientEligibility}</Typography>
            </Box>
          )}
        </Box>
      );
    }

    return null;
  };

  return (
    <Autocomplete
      options={options}
      inputValue={internalInputValue}
      value={searchValue}
      getOptionLabel={(option: UserDTO | EconsultManagedSpecialtyProgram | string) =>
        getOptionLabel(option)
      }
      getOptionDisabled={(user: UserDTO | any) =>
        EconsultUtils.getOutOfOfficeEndDate(user?.unavailability) !== null
      }
      freeSolo
      onChange={(event, newValue: UserDTO | EconsultManagedSpecialtyProgram | string | null) => {
        if (onSelectionChange) {
          if (typeof newValue !== 'string') {
            onSelectionChange(newValue);
          }
        }
      }}
      onInputChange={(event, newInputValue) => {
        setInternalInputValue(newInputValue);
        onInputChange(newInputValue);
      }}
      renderInput={(params) => (
        <Box>
          <EconsultRedirectSpecialistSearchTextField
            {...params}
            ref={inputRef}
            placeholder={t('Case.draft.searchSpecialist') as string}
            onChange={(event) => {
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              event.target.value !== '' && setRequiredErrorMessageStyle(vaildFieldMessageType);
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <InputAdornment position="end">
                  {internalInputValue ? (
                    <IconButton
                      onClick={() => {
                        if (onSelectionChange) {
                          onSelectionChange(null);
                        }
                        setInternalInputValue('');
                        if (inputRef.current) {
                          inputRef.current.focus();
                        }
                      }}>
                      <ClearIcon />
                    </IconButton>
                  ) : null}
                  {params.InputProps.endAdornment}
                </InputAdornment>
              )
            }}
          />

          <Typography style={requiredErrorMessageStyle}>
            {t('Case.patient.field.required')}
          </Typography>
        </Box>
      )}
      filterOptions={(o) => {
        return o;
      }}
      renderOption={(
        renderProps,
        option: UserDTO | EconsultManagedSpecialtyProgram | string,
        { inputValue }
      ) => {
        const optionLabel = getOptionLabel(option);
        const tmcSpecialties =
          typeof option !== 'string' && !('programName' in option) ? option.tmcSpecialties : '';
        const unavailable =
          typeof option !== 'string' && !('programName' in option)
            ? EconsultUtils.getOutOfOfficeEndDate(option.unavailability || '') !== null
            : '';
        const matches = match(optionLabel, inputValue, { insideWords: true });
        const tmcSpecialtyMatches = tmcSpecialties
          ? match(tmcSpecialties, inputValue, { insideWords: true })
          : '';
        const parts = parse(optionLabel, matches);
        const tmcSpecialtyParts = tmcSpecialtyMatches
          ? parse(tmcSpecialties, tmcSpecialtyMatches)
          : [];
        const { key, ...optionProps } = renderProps;
        return (
          <li
            key={key}
            {...optionProps}
            style={{ pointerEvents: unavailable ? 'auto' : undefined }}>
            <HtmlTooltip title={getTooltipInfo(option)} placement="left" arrow>
              <Box
                onClick={(e) => {
                  if (unavailable) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}>
                {parts.map((part: any, index: any) => (
                  <Typography
                    component="span"
                    variant="body2"
                    key={index}
                    sx={{
                      color: getLabelColor(Boolean(unavailable), part.highlight),
                      fontWeight: part.highlight && !unavailable ? 600 : 400
                    }}>
                    {part.text}
                  </Typography>
                ))}
                {tmcSpecialtyParts && tmcSpecialtyParts.length > 0 && (
                  <Box>
                    <Typography variant="body4" color="base.grey4" mr={0.5}>
                      Telemedicine Service:
                    </Typography>
                    {tmcSpecialtyParts.map((part: any, index: any) => (
                      <Typography
                        component="span"
                        variant="body4"
                        key={index}
                        sx={{
                          color: part.highlight ? 'primary.custom5' : 'base.grey4',
                          fontWeight: part.highlight && !unavailable ? 600 : 400
                        }}>
                        {part.text}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            </HtmlTooltip>
          </li>
        );
      }}
      componentsProps={{
        popper: {
          sx: {
            '& .MuiAutocomplete-listbox': {
              fontSize: '0.875rem'
            }
          }
        }
      }}
    />
  );
});

export default EconsultRedirectSpecialistSearch;
